import { ErrorAlert } from '@/components/_ui/ErrorAlert'

import type { CosmicBelowFoldType } from '@/lib/cms/cosmic/types/CosmicBelowFoldType'
import useGlobalStore from '@/store/useGlobalStore'
import { ErrorBoundary } from 'react-error-boundary'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { DistributorBannerSection } from './DistributorBannerSection'
import { FdaStatementSection } from './FdaStatementSection'
import { ProductDescriptionSection } from './ProductDescriptionSection'
import { ProductKeyIngredientsSection } from './ProductKeyIngredientsSection'
import { ProductUsageAndContentsSection } from './ProductUsageAndContentsSection'
import { WhySections } from './WhySections'

type Props = {
  belowFoldSections?: CosmicBelowFoldType
  isAccessory?: boolean
  product: CosmicProductType
  selectedVariation: CosmicVariationType
}

const ErrorFallback = ({ error }: { error: string }) => (
  <div className="mt-10 w-full p-10">
    <ErrorAlert
      error={{
        error: {
          code: 500,
          message: error,
        },
      }}
    />
  </div>
)

export const ProductPageSections = ({
  belowFoldSections,
  isAccessory = false,
  product,
  selectedVariation,
}: Props) => {
  const {
    user: { customer },
  } = useGlobalStore()

  const showDistributorBanner =
    customer?.type === 'Associate' || customer?.type === 'Member'

  if (!belowFoldSections) {
    return <div className="h-40" /> // return this div instead of null to be able to have a space between product and footer
  }

  // Below, you might think that there's a ton of repetitive code, and you would be right!
  // Its very repetitive but there's a reason why.
  // We are loading in different sections of the product page and each section requires data from the CMS
  // The cms data could be corrupted, missing, a relation might have been messed up, a value on the cms
  // could have changed and the frontend code was not updated to reflect that change.
  // Of of those things could cause the page to break, if we just put a try catch block around the entire
  // function, we would not know which section is causing the error.
  // By wrapping each section in an ErrorBoundary, we can catch the error and display an error message
  // specific to that section while still rendering the rest of the page.

  return (
    <div className="max-full w-screen overflow-x-visible md:mt-24">
      <ErrorBoundary
        fallback={
          <ErrorFallback error="Product Description: unexpected error" />
        }
      >
        <ProductDescriptionSection
          productDescription={belowFoldSections.productDescription}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<ErrorFallback error="Key Ingredients: unexpected error" />}
      >
        <ProductKeyIngredientsSection
          keyIngredients={belowFoldSections.keyIngredients}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<ErrorFallback error="Why sections: unexpected error" />}
      >
        <WhySections whySections={belowFoldSections.whySections} />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<ErrorFallback error="Usage & Contents: unexpected error" />}
      >
        <ProductUsageAndContentsSection
          usageAndContents={belowFoldSections.usageAndContents}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={
          <ErrorFallback error="Distributor Banner: unexpected error" />
        }
      >
        {showDistributorBanner && (
          <DistributorBannerSection
            product={product}
            selectedVariation={selectedVariation}
            distributorBanner={belowFoldSections.distributorBanner}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary
        fallback={<ErrorFallback error="FDA Statement unexpected error" />}
      >
        {!isAccessory && <FdaStatementSection {...product} />}
      </ErrorBoundary>
    </div>
  )
}
